import type { ITransactionExportCmd } from "@/data/commands/order/TransactionExportCmd";
import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { PaymentState } from "@/data/model/Enums";
import type { ILocation } from "@/data/model/Location";
import { Misc } from "@/data/util/Misc";

import { BaseModal } from "@/shared/elements/BaseModal";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n } from "@/portal/Trans";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { UiNotification } from "@/portal/ui/Notification";
import {DateTime} from "@/data/util/DateTime";
import { getLocationName } from "@/shared/helpers/LocationHelper";

export class TransactionExportWindow extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private locationSelect: Select<ILocation>;
    private stateSelect: Select<PaymentState>;
    private fromPicker: DatePicker;
    private toPicker: DatePicker;
    private abortController?: AbortController;
    
    constructor() {
        super(i18n("modal.transactionExport.title"), "modal-lg", false);

        this.$element.addClass("transactionexport");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.transactionExport.exportSetting") +
                "</h3></div></div>"
        );

        const locationGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.location") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.locationSelect = new Select<ILocation>(
            undefined,
            Factory.getInstance(LocationDao),
            (c) => getLocationName(c),
            false,
            i18n("modal.transactionExport.selectLocation")
        ).renderTo(locationGroup);

        const stateGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.fields.state") +
                '</label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const select = $('<select class="form-control" />').appendTo(stateGroup);
        Misc.eachEnum(PaymentState, (i, e) => {
            select.append('<option value="' + i + '">' + i18n("enums.PaymentState." + e) + "</option>");
        });
        this.stateSelect = Select.createFromSelect(
            select,
            undefined,
            undefined,
            undefined,
            i18n("modal.transactionExport.allStates")
        );

        const fromToGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.transactionExport.dateFromTo") +
                '</label><div class="col-xl-3 from"></div><div class="col-xl-3 to"></div></div>'
        ).appendTo(createSection);

        const endOfDay = DateTime.getEndOfDay();
        this.fromPicker = new DatePicker(undefined, { endDate: endOfDay }).renderTo(
            fromToGroup.find("> .col-xl-3.from")
        );
        this.toPicker = new DatePicker(undefined, { endDate: endOfDay }).renderTo(fromToGroup.find("> .col-xl-3.to"));

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.export") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onExport();

            return false;
        });
    }

    protected onShow() {}

    protected onHide() {
        this.abortController?.abort();
    }
    
    private onExport() {
        const data: ITransactionExportCmd = {
            exportForLocationId: this.locationSelect.getValue(),
            state: this.stateSelect.getValue() === undefined ? undefined : parseInt(this.stateSelect.getValue()!),
            from: this.fromPicker.getValue(),
            to: this.toPicker.getValue(),
        };

        let hasError = false;
        if (data.from !== undefined && data.to !== undefined && data.from > data.to) {
            this.fromPicker.setError(true);
            this.toPicker.setError(true);
            hasError = true;
        } else {
            this.fromPicker.setError(false);
            this.toPicker.setError(false);
        }

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();

        const abortController = new AbortController();
        this.abortController = abortController;
        
        Factory.getInstance<OrderDao>(OrderDao)
            .exportTransactions(data, abortController.signal)
            .then(() => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                this.close();
            })
            .catch((ex) => {
                logError(ex);
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                window.setTimeout(() => {
                    UiNotification.showException(i18n("modal.transactionExport.failedExport"));
                }, 200);
            });
    }
}
